/**
 * AI agent drawer — the propose-then-confirm chat surface.
 *
 * Mounted once in the app shell. Toggled by the floating ✨ button (also
 * here) or any future keyboard shortcut. Renders a side panel that slides
 * in from the right with a transcript at the top + an input at the bottom.
 *
 * Three message kinds get different cards:
 *   - user → right-aligned bubble
 *   - answer → left-aligned, markdown-ish (we keep it plain for now —
 *     no markdown parser; the Sonnet prompt is tuned for prose)
 *   - proposal → confirm card with workflow + args + reasoning + Run/Cancel
 *   - error → rose-tinted left-aligned bubble
 *
 * The drawer doesn't try to be a full chat app. Single-turn for now; the
 * store doesn't replay history into /propose yet, so each "send" is
 * independent. Multi-turn comes when the propose endpoint accepts a
 * messages array.
 */
import { useEffect, useRef, useState } from "react";
import {
  Sparkles,
  Send,
  X,
  Play,
  ChevronRight,
  Trash2,
  ExternalLink,
  RotateCcw,
  GripVertical,
  Settings as SettingsIcon,
  Copy,
  Check,
  Plus,
} from "lucide-react";
import { useCopy } from "@/lib/use-copy";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { MarkdownView } from "@/components/app/markdown-view";
import {
  useAiAgent,
  AGENT_MODELS,
  type AgentMessage,
  type ReadTraceEntry,
} from "@/stores/ai-agent";
import { cn } from "@/lib/cn";
import { getCookie } from "@/lib/cookies";

const AI_ENABLED_COOKIE = "opus.ai.enabled";

/* ---------- the floating launcher button ---------- */

export function AiAgentLauncher() {
  const open = useAiAgent((s) => s.open);
  const toggle = useAiAgent((s) => s.toggle);
  const [enabled, setEnabled] = useState<boolean>(() => {
    const cookie = getCookie(AI_ENABLED_COOKIE);
    return cookie === null ? true : cookie === "1";
  });

  useEffect(() => {
    const refresh = () => {
      const cookie = getCookie(AI_ENABLED_COOKIE);
      setEnabled(cookie === null ? true : cookie === "1");
    };
    const onCookieChange = (ev: Event) => {
      const detail = (ev as CustomEvent<{ name?: string }>).detail;
      if (!detail || detail.name === AI_ENABLED_COOKIE) refresh();
    };
    window.addEventListener("focus", refresh);
    window.addEventListener("opus:cookie-change", onCookieChange);
    return () => {
      window.removeEventListener("focus", refresh);
      window.removeEventListener("opus:cookie-change", onCookieChange);
    };
  }, []);

  // Global ⌘K (Mac) / Ctrl+K (Win/Linux) toggles the drawer from anywhere.
  // Bound here (not in the drawer) so it works even when collapsed.
  // Uses keydown capture on window so it intercepts before any focused
  // input would consume the keystroke.
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (!enabled) return;
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        toggle();
      }
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [enabled, toggle]);

  // Hide the launcher when the drawer is open — the drawer's close button
  // is the way out.
  if (!enabled || open) return null;
  return (
    <button
      type="button"
      onClick={toggle}
      title="Ask the Opus agent (⌘K / Ctrl+K)"
      className={cn(
        "fixed bottom-6 right-6 z-40",
        "inline-flex h-12 w-12 items-center justify-center rounded-full",
        "bg-gradient-to-br from-violet to-cyan text-white shadow-lg shadow-violet/30",
        "transition-transform hover:scale-105",
      )}
    >
      <Sparkles size={18} />
    </button>
  );
}

/* ---------- the drawer itself ---------- */

export function AiAgentDrawer() {
  const open = useAiAgent((s) => s.open);
  const setOpen = useAiAgent((s) => s.setOpen);
  const [enabled, setEnabled] = useState<boolean>(true);
  const status = useAiAgent((s) => s.status);
  const messages = useAiAgent((s) => s.messages);
  const send = useAiAgent((s) => s.send);
  const abort = useAiAgent((s) => s.abort);
  const execute = useAiAgent((s) => s.execute);
  const cancel = useAiAgent((s) => s.cancel);
  const clear = useAiAgent((s) => s.clear);
  const newConversation = useAiAgent((s) => s.newConversation);
  const model = useAiAgent((s) => s.model);
  const setModel = useAiAgent((s) => s.setModel);
  const maxRounds = useAiAgent((s) => s.maxRounds);
  const setMaxRounds = useAiAgent((s) => s.setMaxRounds);
  const dryRunDefault = useAiAgent((s) => s.dryRunDefault);
  const setDryRunDefault = useAiAgent((s) => s.setDryRunDefault);
  const [settingsOpen, setSettingsOpen] = useState(false);

  const [input, setInput] = useState("");

  useEffect(() => {
    const refresh = () => {
      const cookie = getCookie(AI_ENABLED_COOKIE);
      const next = cookie === null ? true : cookie === "1";
      setEnabled(next);
      if (!next) setOpen(false);
    };
    const onCookieChange = (ev: Event) => {
      const detail = (ev as CustomEvent<{ name?: string }>).detail;
      if (!detail || detail.name === AI_ENABLED_COOKIE) refresh();
    };
    refresh();
    window.addEventListener("focus", refresh);
    window.addEventListener("opus:cookie-change", onCookieChange);
    return () => {
      window.removeEventListener("focus", refresh);
      window.removeEventListener("opus:cookie-change", onCookieChange);
    };
  }, [setOpen]);
  // Auto-scroll the transcript to the bottom on every new message — same
  // pattern as a normal chat UI. Skipped while the user is mid-typing.
  const scrollRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!scrollRef.current) return;
    scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages.length, status]);

  // Drawer width — drag handle on the left edge resizes. Persisted to
  // localStorage so the user's preference survives reloads. Bounded so a
  // wild drag can't shrink it below usability or stretch past the viewport.
  const WIDTH_KEY = "opus.ai-agent.width";
  const MIN_WIDTH = 360;
  const MAX_WIDTH = 900;
  const DEFAULT_WIDTH = 448;
  const [width, setWidth] = useState<number>(() => {
    if (typeof window === "undefined") return DEFAULT_WIDTH;
    const raw = window.localStorage.getItem(WIDTH_KEY);
    const parsed = raw ? parseInt(raw, 10) : NaN;
    return Number.isFinite(parsed)
      ? Math.min(MAX_WIDTH, Math.max(MIN_WIDTH, parsed))
      : DEFAULT_WIDTH;
  });
  useEffect(() => {
    if (typeof window !== "undefined") {
      window.localStorage.setItem(WIDTH_KEY, String(width));
    }
  }, [width]);

  // Drag handler — captures mousedown on the handle, listens to window-level
  // mousemove/mouseup so the drag doesn't drop when the cursor leaves the
  // handle. Width = viewport.right - cursor.x (drawer is anchored right).
  const onResizeMouseDown = (e: React.MouseEvent) => {
    e.preventDefault();
    const onMove = (ev: MouseEvent) => {
      const next = window.innerWidth - ev.clientX;
      setWidth(Math.min(MAX_WIDTH, Math.max(MIN_WIDTH, next)));
    };
    const onUp = () => {
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("mouseup", onUp);
      document.body.style.cursor = "";
      document.body.style.userSelect = "";
    };
    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseup", onUp);
    // Visual feedback for the duration of the drag.
    document.body.style.cursor = "ew-resize";
    document.body.style.userSelect = "none";
  };

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!input.trim() || status === "thinking") return;
    const text = input;
    setInput("");
    await send(text);
  };

  if (!enabled || !open) return null;
  return (
    <dialog
      open
      aria-label="Opus agent"
      style={{ width: `${width}px` }}
      className={cn(
        "fixed inset-y-0 right-0 z-50 m-0 flex max-w-full flex-col border-0 p-0",
        "border-l border-line bg-surface shadow-2xl",
      )}
    >
      {/* Drag handle on the left edge — invisible 6px strip with a centered
          gripper icon shown on hover. Mousedown starts the drag. */}
      <div
        className={cn(
          "absolute left-0 top-0 z-10 flex h-full w-1.5 cursor-ew-resize items-center justify-center",
          "group hover:bg-violet/20",
        )}
      >
        <button
          type="button"
          aria-label="Drag to resize panel"
          onMouseDown={onResizeMouseDown}
          title="Drag to resize"
          className="absolute inset-0 h-full w-full cursor-ew-resize border-none bg-transparent p-0 focus:outline-none"
        />
        <GripVertical
          size={14}
          className="pointer-events-none relative text-fg-mute opacity-0 transition-opacity group-hover:opacity-100"
        />
      </div>
      {/* Header */}
      <div className="flex items-center justify-between border-b border-line px-4 py-3">
        <div className="flex items-center gap-2 font-semibold">
          <Sparkles size={14} className="text-violet" />
          <span>Opus agent</span>
        </div>
        <div className="flex items-center gap-1">
          {/* Model picker — small, unobtrusive. Bare native select to avoid
              pulling Radix for a single dropdown. Disabled while a turn is
              in flight so a mid-call swap can't cause confusion. */}
          <select
            value={model}
            onChange={(e) => setModel(e.target.value as typeof model)}
            disabled={status === "thinking"}
            title="Pick the model used for the next turn"
            className={cn(
              "h-7 rounded-md border border-line bg-surface px-1.5 text-[11px] text-fg-dim",
              "focus:border-violet focus:outline-none disabled:opacity-50",
            )}
          >
            {AGENT_MODELS.map((m) => (
              <option key={m.id} value={m.id}>
                {m.label}
              </option>
            ))}
          </select>
          <Button
            size="sm"
            variant="ghost"
            onClick={() => setSettingsOpen((v) => !v)}
            title="Settings"
            className={cn(settingsOpen && "text-violet")}
          >
            <SettingsIcon size={13} />
          </Button>
          <Button
            size="sm"
            variant="ghost"
            onClick={newConversation}
            title="Start a new conversation (saves the current one to /agent)"
          >
            <Plus size={13} />
          </Button>
          <Button
            size="sm"
            variant="ghost"
            onClick={clear}
            title="Clear the open transcript (server history is kept)"
          >
            <Trash2 size={13} />
          </Button>
          <Button
            size="sm"
            variant="ghost"
            onClick={() => setOpen(false)}
            title="Close"
          >
            <X size={14} />
          </Button>
        </div>
      </div>

      {settingsOpen ? (
        <AgentSettingsPanel
          maxRounds={maxRounds}
          setMaxRounds={setMaxRounds}
          dryRunDefault={dryRunDefault}
          setDryRunDefault={setDryRunDefault}
        />
      ) : null}

      {/* Transcript */}
      <div ref={scrollRef} className="flex-1 overflow-y-auto px-4 py-3">
        {messages.length === 0 ? (
          <EmptyState />
        ) : (
          <div className="flex flex-col gap-3">
            {messages.map((m, i) => (
              <MessageBubble
                key={m.serverId ?? ('sentAt' in m ? m.sentAt : m.receivedAt)}
                m={m}
                onExecute={() => void execute(i)}
                onExecuteDryRun={() => void execute(i, { dryRun: true })}
                onCancel={() => cancel(i)}
                onRerun={
                  m.kind === "user" ? () => void send(m.text) : undefined
                }
                dryRunDefault={dryRunDefault}
              />
            ))}
            {status === "thinking" ? (
              <div className="self-start flex items-center gap-2 rounded-lg bg-surface-tint px-3 py-2 text-[12px] text-fg-mute">
                <Sparkles size={12} className="animate-pulse text-violet" />
                Thinking…
                <button
                  type="button"
                  onClick={abort}
                  className="ml-1 rounded border border-line/60 px-1.5 py-0.5 font-mono text-[10px] text-fg-dim hover:border-rose hover:text-rose"
                  title="Cancel this turn"
                >
                  cancel
                </button>
              </div>
            ) : null}
          </div>
        )}
      </div>

      <AgentInputForm
        input={input}
        setInput={setInput}
        status={status}
        onSubmit={onSubmit}
      />
    </dialog>
  );
}

/* ---------- drawer sub-panels ---------- */

function AgentSettingsPanel({
  maxRounds,
  setMaxRounds,
  dryRunDefault,
  setDryRunDefault,
}: {
  maxRounds: number;
  setMaxRounds: (v: number) => void;
  dryRunDefault: boolean;
  setDryRunDefault: (v: boolean) => void;
}) {
  return (
    <div className="border-b border-line bg-surface-tint/40 px-4 py-3">
      <div className="flex flex-col gap-3 text-[12px]">
        <div className="flex items-center justify-between gap-3">
          <label htmlFor="agent-max-rounds" className="text-fg-dim">
            Max tool rounds
          </label>
          <div className="flex items-center gap-2">
            <input
              id="agent-max-rounds"
              type="range"
              min={1}
              max={20}
              step={1}
              value={maxRounds}
              onChange={(e) => setMaxRounds(parseInt(e.target.value, 10))}
              className="w-32 accent-violet"
            />
            <span className="w-6 text-right font-mono text-fg">
              {maxRounds}
            </span>
          </div>
        </div>
        <div className="flex items-center justify-between gap-3">
          <label htmlFor="agent-dry-default" className="text-fg-dim">
            Default to dry-run
            <div className="text-[10.5px] text-fg-mute">
              Promotes Dry-run to the primary action on proposals.
            </div>
          </label>
          <input
            id="agent-dry-default"
            type="checkbox"
            checked={dryRunDefault}
            onChange={(e) => setDryRunDefault(e.target.checked)}
            className="accent-violet"
          />
        </div>
      </div>
    </div>
  );
}

function AgentInputForm({
  input,
  setInput,
  status,
  onSubmit,
}: {
  input: string;
  setInput: (v: string) => void;
  status: string;
  onSubmit: (e: React.FormEvent) => Promise<void>;
}) {
  return (
    <form
      onSubmit={onSubmit}
      className="border-t border-line bg-surface-tint/50 px-3 py-3"
    >
      <div className="flex items-end gap-2">
        <textarea
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter" && !e.shiftKey) {
              e.preventDefault();
              void onSubmit(e as unknown as React.FormEvent);
            }
          }}
          aria-label="Message to agent"
          rows={2}
          placeholder='e.g. "Disable the guest SSID at NetEng-Lab"'
          className={cn(
            "min-h-[44px] flex-1 resize-none rounded-md border border-line bg-surface px-3 py-2 text-sm",
            "focus:border-violet focus:outline-none",
          )}
          disabled={status === "thinking"}
        />
        <Button
          type="submit"
          size="sm"
          disabled={status === "thinking" || !input.trim()}
        >
          <Send size={13} />
        </Button>
      </div>
      <p className="mt-1.5 text-[10.5px] text-fg-mute">
        Read tools execute immediately. Write tools always wait for your
        confirm.
      </p>
    </form>
  );
}

/* ---------- message bubbles ---------- */

const EXAMPLE_PROMPTS = [
  "Show VLANs on NetEng-Lab",
  "How many devices are offline across all orgs?",
  "Take a backup of NetEng-Lab",
  "Rename VLAN 2 on NetEng-Lab to 'Vendors'",
];

function EmptyState() {
  // Click-to-send the example prompts. Saves the user from typing while
  // they're learning what the agent can do. Uses the same `send` action
  // the input form does, so behaviour is identical.
  const send = useAiAgent((s) => s.send);
  return (
    <div className="flex flex-col items-center gap-3 py-12 text-center">
      <Sparkles size={20} className="text-violet" />
      <div className="text-sm font-semibold">What can I help with?</div>
      <div className="max-w-[300px] text-[12px] text-fg-mute">
        Ask about your Meraki networks or describe a config change. I'll propose
        any destructive action and wait for your confirm before running it.
      </div>
      <div className="mt-2 flex w-full flex-col gap-1.5 px-4 text-[11px] text-fg-dim">
        {EXAMPLE_PROMPTS.map((q) => (
          <button
            key={q}
            type="button"
            onClick={() => void send(q)}
            className="rounded-md bg-surface-tint px-2 py-1.5 text-left transition-colors hover:bg-violet/10 hover:text-fg"
          >
            "{q}"
          </button>
        ))}
      </div>
    </div>
  );
}

function MessageBubble({
  m,
  onExecute,
  onExecuteDryRun,
  onCancel,
  onRerun,
  dryRunDefault,
}: {
  m: AgentMessage;
  onExecute: () => void;
  onExecuteDryRun: () => void;
  onCancel: () => void;
  /** Re-send this user prompt (only meaningful for kind=="user"). */
  onRerun?: () => void;
  /** When true, the proposal action bar promotes Dry-run to the primary
   *  button. Lets safety-conscious users always preview before pushing. */
  dryRunDefault?: boolean;
}) {
  if (m.kind === "user") {
    return (
      <div className="group self-end flex max-w-[80%] items-start gap-1.5">
        {/* Rerun button shows on hover. Useful when the underlying state
            changed and the user wants a fresh answer to the same question. */}
        {onRerun ? (
          <button
            type="button"
            onClick={onRerun}
            title="Re-send this prompt"
            className="mt-1.5 opacity-0 transition-opacity group-hover:opacity-100 text-fg-mute hover:text-violet"
          >
            <RotateCcw size={11} />
          </button>
        ) : null}
        <div className="rounded-2xl rounded-br-md bg-violet/15 px-3 py-2 text-sm text-fg">
          {m.text}
        </div>
      </div>
    );
  }
  if (m.kind === "answer") {
    return (
      <div className="group self-start max-w-[92%]">
        <div className="relative rounded-2xl rounded-bl-md bg-surface-tint px-3 py-2 text-sm text-fg">
          {/* Copy button — appears on hover, top-right of the bubble. Copies
              the raw markdown source so a paste into a doc retains the
              formatting (tables/bold/etc.) the user is looking at. */}
          <CopyButton
            text={m.text}
            className="absolute right-1.5 top-1.5 opacity-0 transition-opacity group-hover:opacity-100"
          />
          <MarkdownView text={m.text} />
        </div>
        <ReadTraceFootnote
          trace={m.readTrace}
          model={m.model}
          elapsedMs={m.elapsedMs}
        />
      </div>
    );
  }
  if (m.kind === "error") {
    return (
      <div className="self-start max-w-[92%] rounded-md border border-rose/30 bg-rose/10 px-3 py-2 text-[12px] text-rose">
        {m.text}
      </div>
    );
  }
  // proposal
  const p = m.proposal;
  return (
    <div className="self-start w-full max-w-[95%] overflow-hidden rounded-xl border border-violet/30 bg-violet/5">
      {/* Reasoning — markdown-rendered so the model's tables/lists look right. */}
      {p.reasoning ? (
        <div className="border-b border-violet/20 bg-violet/10 px-3 py-2 text-[12px] text-fg">
          <MarkdownView text={p.reasoning} />
        </div>
      ) : null}
      {/* Tool + args */}
      <div className="px-3 py-2.5">
        <div className="flex flex-wrap items-center gap-2 text-[11px] text-fg-mute">
          <Sparkles size={11} className="text-violet" />
          <span className="font-mono">propose</span>
          <ChevronRight size={11} />
          <span className="font-mono font-semibold text-fg">{p.tool}</span>
          {/* Per-tool freshness — sourced from latest workflow_runs row.
              Shows "first run ever" when the tool has no prior history. */}
          <span className="ml-auto inline-flex items-center gap-1 rounded-full border border-line/60 bg-surface px-1.5 py-px font-mono text-[9.5px] text-fg-dim">
            {formatLastRun(p.lastRunAt)}
          </span>
        </div>
        <div className="group relative mt-2">
          <pre className="overflow-auto rounded-md border border-line bg-surface px-2.5 py-1.5 pr-9 font-mono text-[10.5px] text-fg-dim">
            {JSON.stringify(p.queuePayload, null, 2)}
          </pre>
          {/* Copy button on hover — drops the exact JSON the worker will
              receive into the clipboard. Useful for sharing or replaying
              outside the agent. */}
          <CopyButton
            text={JSON.stringify(p.queuePayload, null, 2)}
            className="absolute right-1.5 top-1.5 opacity-0 transition-opacity group-hover:opacity-100"
          />
        </div>
        <ReadTraceFootnote
          trace={m.readTrace}
          model={m.model}
          elapsedMs={m.elapsedMs}
        />
      </div>
      {/* Action bar — primary button swaps based on dryRunDefault setting.
          Safety-conscious users get Dry-run promoted; the default-default
          is to put Live Run forward (matches the original behaviour). */}
      <div className="flex items-center justify-end gap-2 border-t border-violet/20 bg-violet/10 px-3 py-2">
        {m.status === "pending" ? (
          <>
            <Button size="sm" variant="ghost" onClick={onCancel}>
              Cancel
            </Button>
            {dryRunDefault ? (
              <>
                <Button size="sm" variant="ghost" onClick={onExecute}>
                  <Play size={11} /> Live Run
                </Button>
                <Button size="sm" onClick={onExecuteDryRun}>
                  Dry-run
                </Button>
              </>
            ) : (
              <>
                <Button size="sm" variant="ghost" onClick={onExecuteDryRun}>
                  Dry-run
                </Button>
                <Button size="sm" onClick={onExecute}>
                  <Play size={11} /> Run
                </Button>
              </>
            )}
          </>
        ) : m.status === "executing" ? (
          <span className="text-[11px] text-fg-mute">Queueing…</span>
        ) : m.status === "executed" ? (
          <a
            href="/workflows"
            className="inline-flex items-center gap-1 text-[11px] text-mint hover:underline"
          >
            queued · {m.runId} <ExternalLink size={10} />
          </a>
        ) : (
          <Badge variant="neutral" size="sm">
            cancelled
          </Badge>
        )}
      </div>
    </div>
  );
}

function ReadTraceFootnote({
  trace,
  model,
  elapsedMs,
}: {
  trace: ReadTraceEntry[];
  model: string;
  elapsedMs: number;
}) {
  if (trace.length === 0) {
    return (
      <div className="mt-1.5 text-[10px] text-fg-mute">
        <span className="font-mono">{model}</span> · {elapsedMs}ms
      </div>
    );
  }
  return (
    <div className="mt-1.5 text-[10px] text-fg-mute">
      <span className="font-mono">{model}</span> · {elapsedMs}ms · called{" "}
      {trace.map((t) => (
        <span
          key={t.tool}
          className="ml-0.5 rounded bg-surface-tint px-1 font-mono"
        >
          {t.tool}
        </span>
      ))}
    </div>
  );
}

/** Render `lastRunAt` (ISO string from the workflow_runs query) as a human
 *  hint. SQLite returns 'YYYY-MM-DD HH:MM:SS' (no T, no tz) for
 *  CURRENT_TIMESTAMP — Date can parse it but treats it as local; we
 *  patch in the 'T' + 'Z' so it's read as UTC, matching what the row
 *  actually represents. */
function formatLastRun(iso: string | null | undefined): string {
  if (!iso) return "first run ever";
  // SQLite literal → ISO 8601 UTC. ISO from elsewhere passes through.
  const norm = iso.includes("T") ? iso : iso.replace(" ", "T") + "Z";
  const t = new Date(norm).getTime();
  if (Number.isNaN(t)) return "—";
  const ms = Date.now() - t;
  if (ms < 0) return "just now";
  const sec = Math.floor(ms / 1000);
  if (sec < 60) return `${sec}s ago`;
  const min = Math.floor(sec / 60);
  if (min < 60) return `last run ${min}m ago`;
  const hr = Math.floor(min / 60);
  if (hr < 48) return `last run ${hr}h ago`;
  const day = Math.floor(hr / 24);
  return `last run ${day}d ago`;
}

function CopyButton({ text, className }: { text: string; className?: string }) {
  const [copied, copy] = useCopy();
  return (
    <button
      type="button"
      onClick={() => void copy(text)}
      title={copied ? "Copied!" : "Copy to clipboard"}
      className={cn(
        "inline-flex h-6 w-6 items-center justify-center rounded border border-line bg-surface text-fg-mute hover:text-violet",
        className,
      )}
    >
      {copied ? <Check size={11} className="text-mint" /> : <Copy size={11} />}
    </button>
  );
}
